import type {
  TradeOrder,
  VybeDealInternal,
  VybeOrderInternal,
} from "@/vybe-trader-client/trade-history/types";

function csvEscapeCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Client-only fields; omit from user-facing CSV exports */
const HISTORY_CSV_OMIT_KEYS = new Set(["syncSource", "syncedAt"]);

function flattenRecord(obj: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (HISTORY_CSV_OMIT_KEYS.has(key)) continue;
    if (val === null || val === undefined) {
      out[key] = "";
    } else if (typeof val === "object") {
      out[key] = JSON.stringify(val);
    } else {
      out[key] = String(val);
    }
  }
  return out;
}

function collectAllKeys(rows: Record<string, string>[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row)) keys.add(k);
  }
  return Array.from(keys).sort();
}

function downloadCsv(filename: string, csvBody: string): void {
  const blob = new Blob([`\uFEFF${csvBody}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function rowsToCsv(rows: Record<string, string>[]): string {
  if (rows.length === 0) return "";
  const headers = collectAllKeys(rows);
  const lines = [
    headers.map(csvEscapeCell).join(","),
    ...rows.map((row) =>
      headers.map((h) => csvEscapeCell(row[h] ?? "")).join(",")
    ),
  ];
  return lines.join("\r\n");
}

export function exportPositionsCsv(
  positions: TradeOrder[],
  filename = "vybe-history-positions.csv"
): void {
  const rows = positions.map((p) => flattenRecord(p as unknown as object));
  downloadCsv(filename, rowsToCsv(rows));
}

export function exportOrdersCsv(
  orders: VybeOrderInternal[],
  filename = "vybe-history-orders.csv"
): void {
  const rows = orders.map((o) => flattenRecord(o as unknown as object));
  downloadCsv(filename, rowsToCsv(rows));
}

export function exportDealsCsv(
  deals: VybeDealInternal[],
  filename = "vybe-history-deals.csv"
): void {
  const rows = deals.map((d) => flattenRecord(d as unknown as object));
  downloadCsv(filename, rowsToCsv(rows));
}
